/**
 * HITL task action calls for the Work Queue inbox (CT-5096).
 *
 * Contract: HITL Tasks API (`hitl.yaml`), verified live 2026-09-04 — see
 * ENDPOINTS-BUG-REPORT.md Área 3. Two facts make this UNLIKE the task /
 * change-request / claim-suggestion services:
 *
 *   1. **Base is PLATFORM, not GS.** HITL lives on `platformApi`
 *      (`VITE_API_BASE_URL`), not `gsApi`.
 *   2. **No GS envelope.** HITL returns raw JSON with REAL HTTP status codes —
 *      success (2xx) → the body directly (`{ task_uuid }` / the resolve result);
 *      error (400/404/502) → `{ error_code, detail, request_id }`. So there is no
 *      `statusCode`/`msgCode` to unwrap: the shared interceptor already resolves on
 *      2xx (returning `response.data`) and rejects on non-2xx with
 *      `{ message, status, raw }` — the useful `error_code`/`detail` live in
 *      `raw.response.data`.
 *
 * The resolve body is `{ contract_id, decision: { status, modified_args? } }`
 * (snake_case). `status ∈ { approved, rejected, modified }`; `modified_args`
 * carries the edited/answer payload for the `modified` case. `contract_id` also
 * accepts the camelCase alias `contractId` server-side, but we send snake_case.
 *
 * Listing: `POST /v1/tasks/hitl/list` (Platform envelope — unlike resolve). Wired into
 * the Work Queue fan-out (CT-5096); assignee is always the auth user. Mock rows
 * without `detail.contractId` still resolve locally.
 */

import { platformApi } from './apiClient.js';
import { validateApiResponse } from '../shared/utils/apiResponse.js';

/** Build a normalized HITL-action Error carrying the HTTP status + `error_code`. */
export function hitlActionError(rejected, fallbackMessage) {
  const data = rejected?.raw?.response?.data;
  const code = data?.error_code ?? null;
  // HITL error bodies carry `detail` (a string, or a pydantic array for 400s).
  const detail = typeof data?.detail === 'string' ? data.detail : null;
  const error = new Error(detail || rejected?.message || fallbackMessage);
  if (rejected?.status != null) error.status = rejected.status;
  if (code) error.code = code;
  return error;
}

/**
 * POST a HITL action. Success (2xx) → the raw response body. Failure → throws an
 * Error with `.status` (the real HTTP status) and `.code` (the HITL `error_code`,
 * e.g. HITL_ERR_TASK_NOT_FOUND / HITL_ERR_DECISION_INVALID / HITL_ERR_RESUME_FAILED).
 */
async function postHitlAction(path, payload, fallbackMessage) {
  try {
    return await platformApi.post(path, payload);
  } catch (rejected) {
    throw hitlActionError(rejected, fallbackMessage);
  }
}

/**
 * Resolve a HITL task — POST /v1/tasks/hitl/resolve. Maps a footer decision to the
 * interrupt `decision`: Approve → `approved`, Block/Decline/Deny → `rejected`,
 * Edit-then-send / Answer → `modified` with `modified_args`.
 *
 * @param {{ contractId: string, status: 'approved'|'rejected'|'modified',
 *           modifiedArgs?: Object }} params
 * @returns {Promise<Object|null>} the resolve result body, or `null` when the
 *   required ids are missing (no request is made).
 */
/**
 * List the calling user's pending HITL tasks — POST /v1/tasks/hitl/list.
 *
 * @param {{ status?: 'pending'|'resolved'|'timed_out'|'all', page?: number,
 *           pageSize?: number }} [params]
 * @returns {Promise<{ items: Object[], meta: Object|null }>}
 */
export async function listHitlTasks({ status = 'pending', page = 1, pageSize = 50 } = {}) {
  try {
    const envelope = await platformApi.post('/v1/tasks/hitl/list', {
      status,
      page,
      page_size: pageSize,
    });
    const { body, meta } = validateApiResponse(envelope, 'Failed to load HITL tasks');
    return { items: Array.isArray(body) ? body : [], meta: meta ?? null };
  } catch (rejected) {
    throw hitlActionError(rejected, 'Failed to load HITL tasks');
  }
}

export async function resolveHitlTask({ contractId, status, modifiedArgs } = {}) {
  if (!contractId || !status) return null;
  const decision = { status };
  if (modifiedArgs != null && Object.keys(modifiedArgs).length > 0) {
    decision.modified_args = modifiedArgs;
  }
  return postHitlAction(
    '/v1/tasks/hitl/resolve',
    { contract_id: contractId, decision },
    'Failed to resolve HITL task'
  );
}
